import React, { useState } from 'react'
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { useFormik } from 'formik';
import { object, string } from 'yup';

// Store
import { Dispatch, RootState } from '../../store/store';

// Constants
import { PASSWORD_LENGTH_LIMITS } from '../../constants/common';

// Utils
import { PASSWORD_RULES_HINT, getPasswordStrengthError } from '../../utils/passwordRules';

// Types
import { ElementSize } from '../../types/element-size';

// Components
import InputControl from '../InputControl/InputControl';
import IconEnvelope from '../Icons/IconEnvelope/IconEnvelope';
import { AuthFormData } from '../../types/auth-form-data';
import SpinnerLoader from '../SpinnerLoader/SpinnerLoader';
import InteractiveButton from '../InteractiveButton/InteractiveButton';

type PasswordFormProps = {
  formData: AuthFormData,
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
  isFirstSignIn: boolean,
} & ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const PasswordForm: React.FC<PasswordFormProps> = (props) => {
  const { formData, isFirstSignIn, signIn, onChange, setPermanentPassword, logOutUser, isAuthLoading } = props;

  // States
  const [isShowPwd, setIsShowPwd] = useState<boolean>(false);

  const loadingClassName = isAuthLoading ? 'flex-auto justify-center items-center' : 'flex-col h-full w-full';
  const passwordFormClassName = `c-password-form flex  ${loadingClassName}`;

  const formik = useFormik({
    initialValues: {
      password_digest: '',
    },
    validationSchema: object({
      password_digest: string()
        .min(PASSWORD_LENGTH_LIMITS.MIN, `Min password length is ${PASSWORD_LENGTH_LIMITS.MIN} characters`)
        .max(PASSWORD_LENGTH_LIMITS.MAX, `Max password length is ${PASSWORD_LENGTH_LIMITS.MAX} characters`)
        .test('password-strength', function (value) {
          if (!isFirstSignIn) return true;

          const error = getPasswordStrengthError(value || '');

          return error ? this.createError({ message: error }) : true;
        })
        .required('Required'),
    }),
    onSubmit: (values) => {
      const customEvent = {
        target: {
          name: 'password_digest',
          value: values.password_digest,
        },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(customEvent);

      if (isFirstSignIn) {
        setPermanentPassword({ email: formData.email, password_digest: values.password_digest });
      } else {
        signIn({ email: formData.email, password_digest: values.password_digest });
      }
    },
  });

  const isDisabledBtn = Boolean(formik.touched.password_digest && formik.errors.password_digest);

  const returnToSignIn = () => {
    logOutUser()
  }

  const changePwdVisibility = () => setIsShowPwd(!isShowPwd);

  return (
    <div className={passwordFormClassName}>
      {!isAuthLoading ? (
        <>
          <form className='flex-auto' onSubmit={formik.handleSubmit}>
            <div className="pb-4">
              <InputControl
                label={`${isFirstSignIn ? 'Enter your new permanent password' : 'Enter your password'}`}
                type="password"
                name='password_digest'
                placeholder="Enter password"
                showPwd={isShowPwd}
                iconRight={<IconEnvelope size={ElementSize.MD} />}
                changePwdVisibility={changePwdVisibility}
                value={formik.values.password_digest}
                customOnChange={formik.handleChange}
                errorText={formik.errors.password_digest}
                showError={formik.touched.password_digest && Boolean(formik.errors.password_digest)}
                suggestion={isFirstSignIn ? PASSWORD_RULES_HINT : ''}
                onBlur={formik.handleBlur}
              />
            </div>
            <InteractiveButton
              text='Please Log In'
              submit
              disabled={isDisabledBtn}
              size={ElementSize.XL}
              className="w-full"
            />
          </form>
          <Link to="#" onClick={returnToSignIn} className="text-primary block w-full text-center">
            Back to Login
          </Link>
        </>
      ) : (
        <SpinnerLoader />
      )}
    </div>
  )
}

const mapState = (state: RootState) => ({
  isAuthLoading: state.auth.isAuthLoading,
});

const mapDispatch = (dispatch: Dispatch) => ({
  setPermanentPassword: dispatch.auth.setPermanentPassword,
  logOutUser: dispatch.auth.logOutUser,
  signIn: dispatch.auth.signIn
});

export default connect(mapState, mapDispatch)(PasswordForm);
